import { fileUrl } from "@/lib/api";
import { formatYearRange } from "@/lib/format";
import { Pencil, Trash2, ImageOff } from "lucide-react";
import { Link } from "react-router-dom";

export default function MovementCard({ movement: m, onEdit, onDelete, compact = false }) {
  const img = fileUrl(m.image_path);
  const years = formatYearRange(m.year_introduced, m.year_discontinued);

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <Link
      to={`/movements/${m.id}`}
      className="group bg-[#F9F0D4] border border-[#8B6A1F]/35 hover:border-[#D4AF37]/40 transition-colors flex flex-col cursor-pointer"
      data-testid={`movement-card-${m.id}`}
    >
      <div className="relative aspect-[4/3] bg-[#EFE3C2] overflow-hidden border-b border-[#8B6A1F]/35">
        {img ? (
          <img src={img} alt={m.movement_id} className="w-full h-full object-contain" loading="lazy" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-[#0A0604]">
            <ImageOff size={28} strokeWidth={1} />
            <span className="text-[10px] uppercase tracking-[0.25em] mt-2 text-[#3A2A10]">No image</span>
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { stop(e); onEdit(); }}
            className="p-1.5 bg-black/70 backdrop-blur-sm text-[#0A0604] hover:text-[#3A2A10] border border-[#8B6A1F]/50"
            data-testid={`mvmnt-edit-${m.id}`}
            aria-label="Edit"
          ><Pencil size={13} /></button>
          <button
            onClick={(e) => { stop(e); onDelete(); }}
            className="p-1.5 bg-black/70 backdrop-blur-sm text-[#0A0604] hover:text-rose-400 border border-[#8B6A1F]/50"
            data-testid={`mvmnt-delete-${m.id}`}
            aria-label="Delete"
          ><Trash2 size={13} /></button>
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-mono-data text-lg text-[#0A0604] leading-tight group-hover:text-[#3A2A10] transition-colors">{m.movement_id}</div>
            <div className="text-xs text-[#3A2A10] mt-0.5">{m.mvmnt_mfg || "—"}</div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {years && (
              <span className="font-mono-data text-[10px] text-[#3A2A10] uppercase tracking-wider border border-[#D4AF37]/20 px-1.5 py-0.5 rounded-sm">
                {years}
              </span>
            )}
            {m.movement_type && (
              <span className="text-[9px] uppercase tracking-wider text-[#3A2A10] bg-[#D4AF37]/15 border border-[#D4AF37]/30 px-1.5 py-0.5 rounded-sm">
                {m.movement_type}
              </span>
            )}
          </div>
        </div>

        {!compact && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
            <Spec label="Size" value={m.size} />
            <Spec label="Jewels" value={m.jewels} mono />
            <Spec label="BPH" value={m.bph} mono />
            <Spec label="Lift" value={m.lift_angle} suffix="°" mono />
            <Spec label="Protection" value={m.protection} />
            <Spec label="Hour hand" value={m.hour_hand_mm} suffix=" mm" mono />
            <Spec label="Min hand" value={m.minute_hand_mm} suffix=" mm" mono />
            <Spec label="Sec hand" value={m.second_hand_mm} suffix=" mm" mono />
          </div>
        )}

        {m.description && (
          <p className="text-xs text-[#3A2A10] leading-relaxed line-clamp-3 pt-2 border-t border-[#8B6A1F]/30">
            {m.description}
          </p>
        )}
      </div>
    </Link>
  );
}

function Spec({ label, value, mono, suffix = "" }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-[0.2em] text-[#3A2A10]">{label}</span>
      <span className={`text-[#0A0604] ${mono ? "font-mono-data" : ""}`}>
        {value === null || value === undefined || value === "" ? <span className="text-[#0A0604]">—</span> : `${value}${suffix}`}
      </span>
    </div>
  );
}
